import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Number from '../Number';
import NumberField from '../fields/NumberField';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';
import TextField from '../fields/TextField';
import ReadonlyField from '../fields/ReadonlyField';
import useMultiValidator from '../../hooks/useMultiValidator';
import { hasPrivilege } from '../../hocs/withPrivileges';
import { auctionType, bidModificationType, bidType } from '../../types';
import { quantityValidator } from '../../validators/quantity';
import { priceValidator } from '../../validators/price';
import './_styles_/BidRow.css';
import { brokerReferenceValidator } from '../../validators/brokerReference';
import {
    isPriceDecrementModificationForbidden,
    isPriceIncrementModificationForbidden,
    joinClassNames,
    onActionKey,
    roundToDecimals,
} from '../../utils';
import useExtendedState from '../../hooks/useExtendedState';
import Obfuscated from './Obfuscated';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import {
    ENERGY,
    getProductType,
    SECURITY,
} from '../../utils/ProductTypes';
import {
    getBiddingType,
    OPEN,
    SLOTTED,
} from '../../utils/BiddingTypes';
import { createUserValue } from '../../utils/bids';
import DateTime, { LONGEST_DATETIME } from '../DateTime';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

export const isAllowedToBidAsBroker = (userPrivileges, bid) => {
    let result;
    if (hasPrivilege(userPrivileges, [Privileges.BID_EDIT_ALL])) {
        result = hasPrivilege(bid.user.permissions, [Privileges.BID_EDIT_AS_BROKER]);
    } else {
        result = hasPrivilege(userPrivileges, [Privileges.BID_EDIT_AS_BROKER]);
    }
    return result;
};

BidRow.propTypes = {
    auction: auctionType.isRequired,
    bid: bidType.isRequired,
    quantityStep: PropTypes.number,
    priceStep: PropTypes.number.isRequired,
    isQuantityStepValid: PropTypes.bool.isRequired,
    isPriceStepValid: PropTypes.bool.isRequired,
    isEditable: PropTypes.bool.isRequired,
    isFinished: PropTypes.bool.isRequired,
    onBidSelect: PropTypes.func,
    onBidModification: PropTypes.func,
    isSelected: PropTypes.bool,
    bidModification: bidModificationType,
};

function BidRow(props) { // NOSONAR javascript:S3776
    const {
        auction,
        bid,
        quantityStep = null,
        priceStep,
        isQuantityStepValid,
        isPriceStepValid,
        isEditable,
        isFinished,
        onBidSelect = null,
        onBidModification = null,
        bidModification = {},
        isSelected = false,
    } = props;
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const canEditAllBids = hasPrivilege(userPrivileges, [Privileges.BID_EDIT_ALL]);
    const productType = getProductType(auction.product.productType);
    const biddingType = getBiddingType(auction.biddingType);
    const [isExpandedManually, setExpandedManually] = useState(false);
    const bidAllowedMinPrice = isPriceDecrementModificationForbidden(auction) ? bid.price : auction.minPrice;
    const bidAllowedMaxPrice = isPriceIncrementModificationForbidden(auction) ? bid.price : auction.maxPrice;
    const minQuantity = auction.smallestUnit;
    const maxQuantity = auction.maxBidSize || auction.quantity;
    const { validators } = useMultiValidator(true, {
        quantity: quantityValidator(t, minQuantity, maxQuantity), // waf validator not required as the input range is validated by quantity validator
        price: compositeValidator(priceValidator(t, bidAllowedMinPrice, bidAllowedMaxPrice), wafValidator(t)),
        brokerReference: compositeValidator(brokerReferenceValidator(t), wafValidator(t)),
    });
    const currentQuantityState = useExtendedState(bid.quantity);
    const onQuantityValidated = (newValue, isValid) => {
        onBidModification && onBidModification(bid.id, 'quantity', newValue, isValid);
    };
    const currentPriceState = useExtendedState(bid.price);
    const onPriceValidated = (newValue, isValid) => {
        onBidModification && onBidModification(bid.id, 'price', newValue, isValid);
    };
    const currentReferenceState = useExtendedState(bid.asBrokerReference);
    const onReferenceValidated = (newValue, isValid) => { // handle second parameter
        onBidModification && onBidModification(bid.id, 'asBrokerReference', newValue, isValid);
    };
    useEffectOnUpdate(() => {
        // handle bid modifications
        if (typeof bidModification.quantity !== 'undefined') {
            currentQuantityState.setValueIfNew(bidModification.quantity);
        } else {
            currentQuantityState.resetIfNecessary();
        }
        if (typeof bidModification.price !== 'undefined') {
            currentPriceState.setValueIfNew(bidModification.price);
        } else {
            currentPriceState.resetIfNecessary();
        }
        if (typeof bidModification.asBrokerReference !== 'undefined') {
            currentReferenceState.setValueIfNew(bidModification.asBrokerReference);
        } else {
            currentReferenceState.resetIfNecessary();
        }
    }, [
        bidModification.quantity,
        bidModification.price,
        bidModification.asBrokerReference,
    ]);
    useEffectOnUpdate(() => {
        // handle new bid values (e.g. after query refetch)
        currentQuantityState.changeInitialState(bid.quantity);
        currentQuantityState.setValueIfNew(bid.quantity);
        currentPriceState.changeInitialState(bid.price);
        currentPriceState.setValueIfNew(bid.price);
        currentReferenceState.changeInitialState(bid.asBrokerReference);
        currentReferenceState.setValueIfNew(bid.asBrokerReference);
    }, [
        bid.quantity,
        bid.price,
        bid.asBrokerReference,
    ]);
    const isExpanded = () => isExpandedManually || bidModification.asBrokerReference;
    const toggleRowExpansion = () => {
        if (!bidModification.asBrokerReference) {
            setExpandedManually(!isExpandedManually);
        }
    };
    const onSelectChangeHandler = (event) => onBidSelect && onBidSelect(bid.id, event.target.checked);
    let modifiedBy;
    if (bid.obfuscated) {
        modifiedBy = <Obfuscated />;
    } else {
        modifiedBy = moment(bid.lastModTime).isSame(bid.creationTime)
            ? <span className="bid-row--no-modification">{t('auctions.bids-list.no-modification')}</span>
            : bid.lastModUser;
    }
    let complementRowColSpan = 4;
    if (productType.is(ENERGY)) {
        complementRowColSpan += 1;
    }
    if (biddingType.is(OPEN)) {
        complementRowColSpan += 1;
    }
    let finishedColumns = null;
    let finishedRowCls = null;
    if (isFinished) {
        if (biddingType.is(OPEN)) {
            complementRowColSpan += 2;
            if (bid.winner) {
                if (bid.quantity === bid.quantityAllocated) {
                    finishedRowCls = 'fully';
                } else {
                    finishedRowCls = 'partially';
                }
                finishedColumns = (
                    <>
                        <td className={`bid-row--quantity-allocated ${finishedRowCls}`}><Number value={bid.quantityAllocated} /></td>
                        <td className={`bid-row--volume ${finishedRowCls}`}><Number value={bid.volume} fractionDigits={2} />{bid.randomlyAllocated && <FontAwesomeIcon icon="registered" />}</td>
                    </>
                );
            } else {
                finishedColumns = (
                    <>
                        <td className="bid-row--quantity-allocated">&nbsp;</td>
                        <td className="bid-row--volume">&nbsp;</td>
                    </>
                );
            }
        } else if (biddingType.is(SLOTTED)) {
            complementRowColSpan += 1;
            let resultClass;
            let resultText;
            if (bid.winner) {
                resultClass = 'accepted';
                resultText = t('auctions.bids-list.accepted');
            } else {
                resultClass = 'rejected';
                resultText = t('auctions.bids-list.rejected');
            }
            finishedColumns = <td className={`bid-row--result readonly ${resultClass}`}>{resultText}</td>;
        }
    }
    if (auction.showRanking) {
        complementRowColSpan++;
    }
    let expandRowCaret;
    if (bidModification.asBrokerReference) {
        expandRowCaret = (
            <FontAwesomeIcon icon="angle-double-down" />
        );
    } else {
        expandRowCaret = (
            <FontAwesomeIcon icon={`caret-${isExpanded() ? 'down' : 'right'}`} />
        );
    }
    let quantityColumn;
    let priceColumn;
    let brokerReferenceFieldValue;
    if (bid.obfuscated) {
        quantityColumn = <Obfuscated className="bid-row--quantity readonly" />;
        priceColumn = <Obfuscated className="bid-row--price readonly" />;
        brokerReferenceFieldValue = <Obfuscated />;
    } else {
        quantityColumn = (
            <span className="bid-row--quantity readonly">
                <Number value={bid.quantity} />
            </span>
        );
        priceColumn = (
            <span className="bid-row--price readonly">
                <Number value={bid.price} fractionDigits={2} />
            </span>
        );
        brokerReferenceFieldValue = (bid.asBrokerReference || '');
    }
    return (
        <>
            <tr className={`bid-row${isSelected ? ' selected' : ''}${bid.cancelled ? ' cancelled' : ''}`}>
                {isEditable && (
                    <td rowSpan={isExpanded() ? 2 : 1}>
                        {!bid.obfuscated && !bid.cancelled && (
                            <input type="checkbox" className="bid-row--selector" checked={isSelected} onChange={onSelectChangeHandler} tabIndex={-1} />
                        )}
                    </td>
                )}
                <td className={joinClassNames('bid-row--user--container', finishedRowCls)}>
                    <div className="bid-row--toggler" onClick={toggleRowExpansion} onKeyUp={onActionKey(toggleRowExpansion)} role="button" tabIndex={-1}>
                        <div className="bid-row--arrow">
                            {expandRowCaret}
                        </div>
                        <span className="bid-row--user readonly">
                            {bid.obfuscated ? <Obfuscated /> : createUserValue(bid, bid.fromVisibleMember || canEditAllBids)}
                        </span>
                    </div>
                </td>
                <td className={joinClassNames('bid-row--modified-by', 'readonly', finishedRowCls)}>
                    {modifiedBy}
                </td>
                {productType.is(ENERGY) && (
                    <td className={joinClassNames('bid-row--balancing-group', 'readonly', finishedRowCls)}>{bid.member.balancingGroup}</td>
                )}
                <td className={joinClassNames('bid-row--member', 'readonly', finishedRowCls)}>{bid.member.memberId}</td>
                {isEditable && !bid.obfuscated && !bid.cancelled ? (
                    <>
                        {biddingType.is(OPEN) && (
                            <td className={joinClassNames('bid-row--quantity--container', bidModification.quantity && 'modified')}>
                                <NumberField
                                    className="bid-row--quantity"
                                    state={currentQuantityState}
                                    validator={validators.quantity}
                                    decrementDisabled={!isQuantityStepValid}
                                    incrementDisabled={!isQuantityStepValid}
                                    onValidated={onQuantityValidated}
                                    step={quantityStep}
                                    min={minQuantity}
                                    max={maxQuantity}
                                />
                            </td>
                        )}
                        <td className={joinClassNames('bid-row--price--container', typeof bidModification.price !== 'undefined' && 'modified')}>
                            <NumberField
                                className="bid-row--price"
                                state={currentPriceState}
                                validator={validators.price}
                                decrementDisabled={!isPriceStepValid || (bidAllowedMinPrice !== null && (roundToDecimals(currentPriceState.value - priceStep) < bidAllowedMinPrice))}
                                incrementDisabled={!isPriceStepValid || (bidAllowedMaxPrice !== null && (roundToDecimals(currentPriceState.value + priceStep) > bidAllowedMaxPrice))}
                                onValidated={onPriceValidated}
                                step={priceStep}
                                min={bidAllowedMinPrice}
                                max={bidAllowedMaxPrice}
                                fractionDigits={2}
                            />
                        </td>
                    </>
                ) : (
                    <>
                        {biddingType.is(OPEN) && (
                            <td className={joinClassNames('bid-row--quantity--container', finishedRowCls)}>
                                {quantityColumn}
                            </td>
                        )}
                        <td className={joinClassNames('bid-row--price--container', finishedRowCls)}>
                            {priceColumn}
                        </td>
                    </>
                )}
                {auction.showRanking && (
                    <td className={joinClassNames('bid-row--rank--container', finishedRowCls)}>
                        {bid.obfuscated ? <Obfuscated className="bid-row--rank readonly" /> : (
                            <span className="bid-row--rank readonly">
                                {bid.rank || `> ${auction.quantity}`}
                            </span>
                        )}
                    </td>
                )}
                {finishedColumns}
            </tr>
            <tr className={`bid-row-complement ${isExpanded() ? 'expanded' : ''}${bid.cancelled ? ' cancelled' : ''}`}>
                <td colSpan={complementRowColSpan} className={bidModification.asBrokerReference ? 'modified' : null}>
                    <div className="bid-row-complement--container">
                        <ReadonlyField
                            label={t('auctions.bids-list.bid-row.id')}
                            value={bid.obfuscated ? <Obfuscated /> : bid.id}
                            className="bid-row-complement--id"
                        />
                        <ReadonlyField
                            label={t('auctions.bids-list.bid-row.creation-time')}
                            value={bid.obfuscated ? <Obfuscated /> : <DateTime value={bid.creationTime} format={LONGEST_DATETIME} />}
                            className="bid-row-complement--creation-time"
                        />
                        <ReadonlyField
                            label={t('auctions.bids-list.bid-row.last-modified')}
                            value={bid.obfuscated ? <Obfuscated /> : <DateTime value={bid.lastModTime} format={LONGEST_DATETIME} />}
                            className="bid-row-complement--last-mod-time"
                        />
                        {productType.is(SECURITY) && (
                            (isEditable && !bid.obfuscated && isAllowedToBidAsBroker(userPrivileges, bid)) ? (
                                <TextField
                                    className="bid-row-complement--reference"
                                    label={t('auctions.bids-list.bid-row.reference')}
                                    state={currentReferenceState}
                                    validator={validators.brokerReference}
                                    onValidated={onReferenceValidated}
                                />
                            ) : (
                                <ReadonlyField
                                    className="bid-row-complement--reference"
                                    label={t('auctions.bids-list.bid-row.reference')}
                                    value={brokerReferenceFieldValue}
                                />
                            )
                        )}
                    </div>
                </td>
            </tr>
        </>
    );
}

export default BidRow;
